import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, filter, tap, switchMap, take } from 'rxjs/operators';
import * as DashboardActions from './dashboard.actions';
import * as AuthActions from '../auth/auth.actions';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { generateCacheKey } from './dashboard.state';
import { selectWidgetData, selectWidgetDataLastUpdated, selectCurrentUserId } from './dashboard.selectors';
import { AppState } from '../reducers';
import { AuthService } from '../../services/auth/auth.service';
import { WidgetSettings } from '../../components/duna/duna-dashboard/duna-dashboard.component';

// Cache TTL in milliseconds - 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

// User widget configs storage key
const USER_WIDGET_CONFIGS_KEY = 'duna_user_widget_configs';

@Injectable()
export class DashboardEffects {
  // Set current user when auth state changes (use action.user — getUserId() can lag behind loginSuccess)
  setCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.loginSuccess,
        AuthActions.refreshTokenComplete,
        AuthActions.initializeAuthSuccess
      ),
      switchMap((action) => {
        const user = 'user' in action ? action.user : null;
        const userIdFromAction =
          user?.id != null ? String(user.id) : null;

        if (userIdFromAction) {
          return of(DashboardActions.setCurrentUser({ userId: userIdFromAction }));
        }

        return this.authService.getUserId().pipe(
          take(1),
          map((userId) =>
            userId
              ? DashboardActions.setCurrentUser({ userId })
              : { type: '[Dashboard] No User ID Available' }
          ),
          catchError(() => of({ type: '[Dashboard] Set Current User Error' }))
        );
      })
    )
  );

  // Load user widget configs when user is set
  loadUserWidgetConfigs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.setCurrentUser),
      switchMap(({ userId }) => {
        // Try to load from localStorage first
        try {
          const storedData = localStorage.getItem(`${USER_WIDGET_CONFIGS_KEY}_${userId}`);
          if (storedData) {
            const configs = JSON.parse(storedData);
            return of(DashboardActions.loadUserWidgetConfigsSuccess({ userId, configs }));
          }
        } catch (error) {
          console.error('[DashboardEffects] Error loading user widget configs from localStorage:', error);
        }
        
        // If no local storage data, return empty configs
        return of(DashboardActions.loadUserWidgetConfigsSuccess({ userId, configs: {} }));
      })
    )
  );

  // Save user widget config
  saveUserWidgetConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.saveUserWidgetConfig),
      withLatestFrom(this.store.select(selectCurrentUserId)),
      tap(([action, currentUserId]) => {
        if (action.userId !== currentUserId) {
          console.warn(`[DashboardEffects] Attempting to save widget config for user ${action.userId} but current user is ${currentUserId}`);
        }
        
        try {
          // Get existing configs from localStorage
          const storageKey = `${USER_WIDGET_CONFIGS_KEY}_${action.userId}`;
          const existingDataStr = localStorage.getItem(storageKey) || '{}';
          const existingData = JSON.parse(existingDataStr);
          
          // Update with new config
          const updatedData = {
            ...existingData,
            [action.widgetId]: {
              settings: action.settings,
              position: action.position
            }
          };
          
          // Save back to localStorage
          localStorage.setItem(storageKey, JSON.stringify(updatedData));
          
        } catch (error) {
          console.error('[DashboardEffects] Error saving user widget config to localStorage:', error);
        }
      }),
      map(([action]) => DashboardActions.saveUserWidgetConfigSuccess({ 
        userId: action.userId, 
        widgetId: action.widgetId 
      })),
      catchError((error) => of(DashboardActions.saveUserWidgetConfigFailure({ 
        userId: 'unknown', 
        widgetId: 'unknown', 
        error 
      })))
    )
  );

  loadDashboardWidgetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDashboardWidgetData),
      withLatestFrom(
        this.store.select(state => state),
      ),
      filter(([action, state]) => {
        // If force refresh is true, always fetch
        if (action.forceRefresh) {
          return true;
        }

        // Generate cache key
        const cacheKey = generateCacheKey(action.widgetType, action.dataSource, action.filters);
        
        // Check if data exists in store
        const cachedData = selectWidgetData(
          action.widgetType, 
          action.dataSource, 
          action.filters
        )(state);
        
        // Check last updated time
        const lastUpdated = selectWidgetDataLastUpdated(
          action.widgetType, 
          action.dataSource, 
          action.filters
        )(state);
        
        // If data doesn't exist, fetch it
        if (!cachedData) {
          return true;
        }
        
        // If data is old (older than CACHE_TTL), fetch it
        if (lastUpdated && Date.now() - lastUpdated > CACHE_TTL) {
          return true;
        }
        
        // If we have valid cached data, return it
        this.store.dispatch(DashboardActions.loadDashboardWidgetDataSuccess({
          widgetType: action.widgetType,
          dataSource: action.dataSource,
          filters: action.filters,
          data: cachedData
        }));
        
        return false;
      }),
      mergeMap(([action]) =>
        this.dashboardService.getWidgetData(action.widgetType, action.dataSource, action.filters).pipe(
          map(data => {
            return DashboardActions.loadDashboardWidgetDataSuccess({
              widgetType: action.widgetType,
              dataSource: action.dataSource,
              filters: action.filters,
              data
            });
          }),
          catchError(error => {
            console.error(`[DashboardEffects] Error loading data for ${action.widgetType}:`, error);
            return of(DashboardActions.loadDashboardWidgetDataFailure({ 
              widgetType: action.widgetType,
              dataSource: action.dataSource,
              filters: action.filters,
              error 
            }));
          })
        )
      )
    )
  );

  refreshAllWidgetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.refreshAllDashboardWidgetData),
      // Don't dispatch any actions from this effect - widgets will dispatch their own loadData actions
      // with forceRefresh=true when they need to refresh
    ),
    { dispatch: false }
  );

  // Clear user widget configs on logout
  clearUserDataOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType('[Auth] Logout'),
      withLatestFrom(this.store.select(selectCurrentUserId)),
      mergeMap(([_, userId]) => {
        if (userId) {
          return [
            DashboardActions.clearUserWidgetConfigs({ userId }),
            DashboardActions.clearDashboardWidgetData({})
          ];
        }
        return [DashboardActions.clearDashboardWidgetData({})];
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {}
} 